import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CompanyDomain } from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import Button from '@lib/components/Button/Button';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Table from '@lib/components/Table/Table';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import Trans from '@lib/components/Trans/Trans';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import { IconName } from '@lib/components/Modal/enums';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import AddDomainModalForm from './components/AddDomainModalForm/AddDomainModalForm';
import VerifyDomainModal from './components/VerifyDomainModal/VerifyDomainModal';
import getTableActionsMenu from './utils/getTableActionsMenu';
import getTableColumns from './utils/getTableColumns';
import useActions from './utils/useActions';
import styles from './CompanyDomains.module.scss';

const modals = [
  Modals.CompanyDomainDestroyModal,
  Modals.CompanyDomainFormModal,
  Modals.CompanyDomainVerifyModal,
];

export default function CompanyDomains() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const useModalManagementResult = useModalManagement(modals);
  const { closeModal, isModalOpened } = useModalManagementResult;
  const {
    companyDomainsQueryHookResult,
    createMutationResult,
    isDomainUsedBySso,
    onAddDomain,
    onConfirmedDelete,
    onConfirmedVerify,
    onDelete,
    onSubmitAddDomain,
    onVerify,
    selectedItem,
    verifyMutationLoading,
  } = useActions({
    useModalManagementResult,
  });

  const tableActionsMenu = getTableActionsMenu({
    t,
    onVerify,
    onDelete,
  });
  const tableColumns = getTableColumns({ t });

  const tableEmptyState = (
    <ListEmptyState
      icon={IconName.DomainVerification}
      title={t('no-domains-added-yet')}
      text={<Trans i18nKey="no-domains-added-text" />}
      actions={[
        <Button
          key="add"
          buttonText={t('add-domain')}
          leftIcon="add"
          onClick={() => onAddDomain()}
        />,
      ]}
    />
  );

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      <PageBody withoutHeader>
        <WhiteBox>
          <DashboardTitleHeader
            actions={[
              <Button
                key="add"
                buttonText={t('add-domain')}
                leftIcon="add"
                onClick={() => onAddDomain()}
              />,
            ]}
            title={t('domains')}
          />
          {companyDomainsQueryHookResult.firstLoading ? (
            <CircleLoader />
          ) : (
            <Table<CompanyDomain>
              actionsMenu={tableActionsMenu}
              columns={tableColumns}
              data={companyDomainsQueryHookResult.collection}
              emptyState={tableEmptyState}
              fullHeight
              id="company-domains"
              loading={companyDomainsQueryHookResult.loading}
              totalEntries={companyDomainsQueryHookResult.totalCount}
            />
          )}
        </WhiteBox>
      </PageBody>
      <AddDomainModalForm
        graphQLErrors={createMutationResult.error?.graphQLErrors}
        isOpen={isModalOpened(Modals.CompanyDomainFormModal)}
        loading={createMutationResult.loading}
        onClose={() => closeModal(Modals.CompanyDomainFormModal)}
        onSubmit={onSubmitAddDomain}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-domain-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.CompanyDomainDestroyModal)}
        onClose={() => closeModal(Modals.CompanyDomainDestroyModal)}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        modalContentClassName={styles.modalContentClassName}
        useProgress
      >
        {isDomainUsedBySso ? (
          <SupportiveMessage
            color={SupportiveMessageColor.Yellow}
            type={SupportiveMessageType.FilledWithLeftBorder}
            text={t('domain-used-by-sso-warning')}
          />
        ) : undefined}
      </ConfirmationModal>
      <VerifyDomainModal
        dnsTxtRecord={selectedItem?.dnsTxtRecord || ''}
        isOpen={isModalOpened(Modals.CompanyDomainVerifyModal)}
        loading={verifyMutationLoading}
        onClose={() => closeModal(Modals.CompanyDomainVerifyModal)}
        onVerify={onConfirmedVerify}
      />
    </>
  );
}
